import React, { useState, createContext, useContext, ReactNode, ElementType, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';

interface DropdownContextType {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleOpen: () => void;
}

const DropDownContext = createContext<DropdownContextType | undefined>(undefined);

interface DropdownProps {
  children?: ReactNode;
  as?: ElementType;
  className?: string;
}

const Dropdown = ({ as: Component = 'div', children, className }: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => {
    setOpen((previousState) => !previousState);
  }, []); // Empty dependency array

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (open && toggleOpen) {
          toggleOpen();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [open, toggleOpen]); // Include toggleOpen as a dependency

  return (
    <DropDownContext.Provider value={{ open, setOpen, toggleOpen }}>
      <Component
        ref={dropdownRef}
        className={`dropdown ${className}`}
      >
        {children}
      </Component>
    </DropDownContext.Provider>
  );
};

interface TriggerProps {
  children: ReactNode;
  type?: ElementType;
  className?: string;
  id?: string;
  href?: any;
}

export const Trigger: React.FC<TriggerProps> = ({ type, children, className, id }) => {
  const { open, toggleOpen } = useContext(DropDownContext)!;

  const getClassNameButton = className ? className : "bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded";
  const getClassNameLink = className ? className : "transition-all duration-200 ease-linear bg-white border-dashed dropdown-toggle text-custom-500 btn border-custom-500 hover:text-custom-500 hover:bg-custom-50 hover:border-custom-600 focus:text-custom-600 focus:bg-custom-50 focus:border-custom-600 active:text-custom-600 active:bg-custom-50 active:border-custom-600 dark:focus:ring-custom-400/20 dark:bg-custom-400/20 ";
  return (
    <>
      {type === 'a' ? (
        <Link id={id} to="/#" onClick={(e: any) => {
          e.preventDefault();
          toggleOpen();
        }} className={getClassNameLink + (open ? " show" : "")}>
          {children}
        </Link>
      ) : (
        <button id={id} onClick={toggleOpen} className={getClassNameButton}>
          {children}
        </button>
      )}
    </>
  );
};


interface ContentProps {
  as?: ElementType;
  align?: 'left' | 'right';
  className?: string;
  width?: string;
  contentClasses?: string;
  children: ReactNode;
  placement?: 'right-end' | 'start-end' | 'top-end' | 'bottom-start' | 'bottom-end' | 'top-start';
}

const Content: React.FC<ContentProps> = ({ as: Component = 'div', className, children, placement }) => {
  const { open, setOpen } = useContext(DropDownContext)!;

  const getClassName = className || "absolute z-50 py-2 mt-1 text-left list-none bg-white rounded-md shadow-md dropdown-menu min-w-max dark:bg-zink-400";

  const [placementState, setPlacement] = useState('right-end' as 'right-end' | 'start-end' | 'top-end' | 'bottom-start' | 'bottom-end' | 'top-start');

  useEffect(() => {
    if (placement) setPlacement(placement);
  }, [placement]); // Add dependency array to useEffect

  const dropdownElementRef = useRef<HTMLDivElement>(null);

  const isRtl = document.getElementsByTagName("html")[0].getAttribute("dir") === "rtl";

  // Returned as a style object rather than written onto the ref: the content is
  // unmounted while closed, so the ref is still null on the render that opens it
  // and any imperative styling would never land.
  const getDropdownStyle = (): React.CSSProperties => {
    if (!open) return {};

    const base: React.CSSProperties = { position: 'absolute', margin: '0px' };

    switch (placementState) {
      case 'right-end':
        // Pin the menu's inline-end edge to the trigger so it opens inwards and
        // never overflows the viewport.
        return { ...base, inset: isRtl ? '0px auto auto 0px' : '0px 0px auto auto', transform: 'translate(0px, 54px)' };
      case 'start-end':
        return { ...base, inset: '0px auto auto 0px', transform: 'translate(0px, 20px)' };
      case 'top-end':
        return { ...base, inset: 'auto 0px 0px auto', transform: 'translate(-58px, -30px)' };
      case 'bottom-start':
        return { ...base, inset: '0px 0px auto auto', transform: 'translate(0px, 54px)' };
      case 'bottom-end':
        return { ...base, inset: '0px 0px auto auto', transform: 'translate(0px, 39px)' };
      case 'top-start':
        return { ...base, inset: 'auto auto 0px 0px', transform: 'translate(0px, -95px)' };
      default:
        return {};
    }
  };

  return (
    // Headless UI v2 no longer passes a transition status string to the render
    // prop (it hands back an empty bag), so `open` drives the class instead.
    // No enter/leave classes are configured here, so the two are equivalent.
    <Transition
      as={React.Fragment}
      show={open}
    >
      <Component
        ref={dropdownElementRef}
        onClick={() => setOpen(false)}
        className={`${getClassName} ${open ? 'transition-all' : ''}`}
        style={getDropdownStyle()}
      >
        {children}
      </Component>
    </Transition>
  );
};

Dropdown.Trigger = Trigger;
Dropdown.Content = Content;

export { Dropdown };
